import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, MessageSquare, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSwipeBack } from "@/hooks/useSwipeBack";
import { Analytics } from "@/lib/analytics";
import { useTranslation } from "react-i18next";

const SUPPORT_EMAIL = "macksterz98@gmail.com";

export const Support = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  useSwipeBack();
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !subject.trim() || !message.trim()) {
      toast.error(t('support.validation.fillAll'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error(t('support.validation.invalidEmail'));
      return;
    }

    setSending(true);
    
    try {
      const { error } = await supabase.functions.invoke('send-support-email', {
        body: {
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim()
        }
      });

      if (error) throw error;

      Analytics.supportContacted();
      toast.success(t('support.send.success'));
      setSubject("");
      setMessage("");
    } catch (error) {
      console.error('Error sending support email:', error);
      toast.error(t('support.send.error', { email: SUPPORT_EMAIL }));
    } finally {
      setSending(false);
    }
  };

  return (
    <div 
      className="bg-gradient-bg p-4"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)',
      }}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{t('support.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('support.subtitle')}</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-normal">
              <MessageSquare className="h-5 w-5 text-primary" />
              {t('support.contact.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-muted-foreground text-sm">
              {t('support.contact.intro')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('support.form.emailLabel')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('support.form.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={255}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">{t('support.form.subjectLabel')}</Label>
                <Input
                  id="subject"
                  placeholder={t('support.form.subjectPlaceholder')}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">{t('support.form.messageLabel')}</Label>
                <Textarea
                  id="message"
                  placeholder={t('support.form.messagePlaceholder')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  maxLength={2000}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {t('support.form.charCount', { current: message.length, max: 2000 })}
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full"
                disabled={sending || !subject.trim() || !message.trim()}
              >
                {sending ? (
                  t('support.form.sending')
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('support.form.submit')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-medium">{t('support.directEmail')}</p>
                <a 
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-sm text-primary hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
